import {MutableRefObject, RefObject, useEffect, useRef} from 'react';
import {SlideDirection} from '../../../carousel/SlideDirection.enum';
import {LEFT_SIDE_GAP_PX} from './CarouselOptions.interface';
import {getCalculatedCarouselTranslateX} from './getCalculatedCarouselTranslateX.function';

interface Params {
  isLeftSideMode: boolean;
  carouselRef: RefObject<HTMLDivElement>;
  centerPadding: string;
  currentIndex: number;
  lastIndex: number;
  onTransitionCarouselRef: MutableRefObject<boolean>;
  slideSpeed: number;
  cloneElementCount: number;
  slideDirectionRef: MutableRefObject<SlideDirection>;
}

export function useCarouselAnimation({
  isLeftSideMode,
  carouselRef,
  currentIndex,
  lastIndex,
  slideSpeed,
  onTransitionCarouselRef,
  centerPadding,
  cloneElementCount,
  slideDirectionRef,
}: Params) {
  const prevIndexRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    return () => {
      prevIndexRef.current = currentIndex;
    };
  }, [currentIndex]);
  useEffect(() => {
    const prevIndex = prevIndexRef.current;
    const realCurrentItemIndex = currentIndex + cloneElementCount;
    const carousel = carouselRef.current;

    if (!carousel || prevIndex === undefined) {
      return;
    }
    if (prevIndex === currentIndex) {
      carousel.style.transform = `translateX(${getCalculatedCarouselTranslateX(
        realCurrentItemIndex,
        centerPadding
      )})`;
      return;
    }
    // transition이 끝나면 위에 가상슬라이드로 이동했던 녀석들을 되돌려주기 위해 transition을 끄고 제 위치로 되돌려주는 이벤트핸들러.(실제 화면에는 아무 변동 없는 것 처럼 보임)
    function onTransitionEnd() {
      if (carousel) {
        carousel.style.transition = 'none';
        if (isLeftSideMode && realCurrentItemIndex === 0) {
          carousel.style.transform = `translateX(${LEFT_SIDE_GAP_PX}px)`;
        } else {
          carousel.style.transform = `translateX(${getCalculatedCarouselTranslateX(
            realCurrentItemIndex,
            centerPadding
          )})`;
        }
      }
      onTransitionCarouselRef.current = false;
    }

    onTransitionCarouselRef.current = true;
    carousel.style.transition = `transform ${slideSpeed / 1000}s`;
    const isEndToFirst =
      prevIndex === lastIndex &&
      currentIndex === 0 &&
      slideDirectionRef.current === SlideDirection.Right;
    const isFirstToEnd =
      prevIndex === 0 &&
      currentIndex === lastIndex &&
      slideDirectionRef.current === SlideDirection.Left;
    // 끝에서 처음 슬라이드로 가는 경우, 끝 슬라이드 뒤에 복제해 놓은 슬라이드로 이동한다.  C/A/B/C/A 형태라면 4번 -> 5번 위치로 이동)
    if (isEndToFirst) {
      carousel.style.transform = `translateX(${getCalculatedCarouselTranslateX(
        lastIndex + cloneElementCount + 1,
        centerPadding
      )})`;
    }
    // 처음에서 끝 슬라이드로 가는 경우, 첫 슬라이드 앞에 복제해놓은 슬라이드로 이동한다. ( C/A/B/C/A 형태라면 2번 -> 1번 위치로 이동)
    else if (isFirstToEnd) {
      carousel.style.transform = `translateX(${getCalculatedCarouselTranslateX(
        cloneElementCount - 1,
        centerPadding
      )}`;
    }
    // 그 외 상황인 경우 현재 위치를 그대로 보여주면 된다
    else {
      if (isLeftSideMode && realCurrentItemIndex === 0) {
        carousel.style.transform = `translateX(${LEFT_SIDE_GAP_PX}px)`;
      } else {
        carousel.style.transform = `translateX(${getCalculatedCarouselTranslateX(
          realCurrentItemIndex,
          centerPadding
        )})`;
      }
    }
    carousel.addEventListener('transitionend', onTransitionEnd, {once: true});
    return () => {
      onTransitionEnd();
      carousel.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [
    currentIndex,
    slideSpeed,
    lastIndex,
    cloneElementCount,
    centerPadding,
    onTransitionCarouselRef,
    carouselRef,
    slideDirectionRef,
    isLeftSideMode,
  ]);
}
